/**
 * Console overview.
 *
 * Read-only counters. Click totals come from the daily rollup, so the page says
 * how far they have been folded in rather than pretending to be live; signups
 * are written the moment an account is created and are always current.
 */

import { useTranslation } from 'react-i18next'

import { LoadingBlock, ErrorBlock } from '../../components/layout/states'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Stat, StatGrid } from '../../components/ui/primitives'
import { useAdminSummary } from '../../hooks/use-admin'
import { formatUnixSeconds } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { AdminPage } from './shared'

export function AdminOverviewPage() {
  const { t } = useTranslation()
  const summary = useAdminSummary()
  const locale = currentLocale()

  return (
    <AdminPage title={t('admin.overview.title')} current={t('admin.title')}>
      {summary.isPending ? <LoadingBlock label={t('common.loading')} /> : null}
      {summary.isError ? (
        <ErrorBlock
          title={t('admin.common.error')}
          retryLabel={t('admin.common.retry')}
          onRetry={() => void summary.refetch()}
        />
      ) : null}

      {summary.data ? (
        <>
          <StatGrid>
            <Stat label={t('admin.overview.pendingApplications')} value={summary.data.pendingApplications} />
            <Stat label={t('admin.overview.approvedLast7Days')} value={summary.data.approvedLast7Days} />
            <Stat label={t('admin.overview.rejectedLast7Days')} value={summary.data.rejectedLast7Days} />
            <Stat label={t('admin.overview.availableCodes')} value={summary.data.availableCodes} />
          </StatGrid>

          <StatGrid>
            <Stat label={t('admin.overview.newUsersLast7Days')} value={summary.data.newUsersLast7Days} />
            <Stat label={t('admin.overview.referredSignups')} value={summary.data.referredSignups} />
            <Stat
              label={t('admin.overview.clicksTotal')}
              // Before the first rollup the counter is genuinely unknown, and a
              // plain 0 reads as "nobody clicked" instead of "not counted yet".
              value={
                summary.data.clicksAggregatedUntil === null
                  ? t('admin.common.noData')
                  : summary.data.clicksTotal
              }
            />
          </StatGrid>

          <Card>
            <CardHeader>
              <CardTitle>{t('admin.overview.clickFreshnessTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground text-sm">
              {summary.data.clicksAggregatedUntil === null
                ? t('admin.overview.clickNeverAggregated')
                : t('admin.overview.clickAggregatedUntil', {
                    time: formatUnixSeconds(summary.data.clicksAggregatedUntil, locale),
                  })}
            </CardContent>
          </Card>
        </>
      ) : null}
    </AdminPage>
  )
}
